import isEmpty from 'lodash/isEmpty';
import {
    type ConfigurableProduct,
    type ConfigurableProductOptions,
    type SelectedConfigurableOption,
    type CartItemInterface,
    type ConfigurableVariant,
    type Maybe,
    CurrencyEnum,
} from '../types/front.graphql';
import { getFinalPrice, type ProductModel } from '@kidslabs/module-catalog';
import { type ConfigurableCartItemLocal, type SimpleCartItemLocal } from './cart.model';
import { uuidGenerator } from '../utils/uuid-generator';
import { NewCartItemIdGenerator } from '../utils/new-cart-item-id-generator';

const findAttribute = (attributeCode: string, configurableProductOptions: ConfigurableProductOptions[]) => {
    return configurableProductOptions.find(option => option.attribute_code === attributeCode);
};

const generateSimpleCartItem = (item: ProductModel, quantity: number = 1): SimpleCartItemLocal => {
    const finalPrice = getFinalPrice(item);
    const currencyValue = (finalPrice?.currency ?? CurrencyEnum.Vnd) as CurrencyEnum;
    return {
        localId: uuidGenerator.generate(),
        id: NewCartItemIdGenerator.generateFromItemId(item?.id ?? 'no-product-id'), // Use utility instead of hardcode
        quantity,
        product: item as unknown as SimpleCartItemLocal['product'],
        prices: {
            discounts: null,
            price: {
                currency: currencyValue,
                value: finalPrice?.value,
                __typename: 'Money',
            },
            row_total: {
                currency: currencyValue,
                value: finalPrice?.value,
                __typename: 'Money',
            },
            row_total_including_tax: {
                currency: currencyValue,
                value: finalPrice?.value,
                __typename: 'Money',
            },
            total_item_discount: {
                currency: CurrencyEnum.Vnd,
                value: 0,
                __typename: 'Money',
            },
            __typename: 'CartItemPrices',
        },
    };
};

const generateConfigurableCartItem = (
    item: ConfigurableProduct,
    variation: ConfigurableVariant,
    quantity: number = 1
): ConfigurableCartItemLocal => {
    const { id: itemId, name, configurable_options: initConfigurableOptions } = item;
    const { product } = variation;
    const configurableProductOptions = initConfigurableOptions as ConfigurableProductOptions[];
    const variantProduct = {
        ...(product as ProductModel),
    };
    const finalPrice = getFinalPrice(variantProduct);
    const currencyValue = (finalPrice?.currency ?? CurrencyEnum.Vnd) as CurrencyEnum;
    const { attributes } = variation;
    const configurableOptions: SelectedConfigurableOption[] = [];
    attributes?.forEach(attributeInner => {
        const configurableProductOption = findAttribute(attributeInner?.code ?? '', configurableProductOptions);
        if (configurableProductOption && attributeInner) {
            configurableOptions.push({
                id: +(configurableProductOption.attribute_id ?? 0),
                option_label: configurableProductOption?.label ?? 'No Option Label',
                value_id: attributeInner?.value_index ?? 0,
                value_label: attributeInner.label ?? 'No Value Label',
            });
        }
    });
    variantProduct.name = name;
    return {
        localId: uuidGenerator.generate(),
        id: NewCartItemIdGenerator.generateForConfigurable(
            `${itemId ?? 'no-configurable-product-id'}`,
            `${product?.id ?? 'no-variant-product-id'}`
        ), // Use utility instead of hardcode
        quantity,
        product: item as unknown as ConfigurableCartItemLocal['product'],
        configurable_options: configurableOptions,
        customizable_options: [],
        configured_variant: variantProduct as unknown as ConfigurableCartItemLocal['configured_variant'],
        prices: {
            discounts: [
                {
                    amount: {
                        value: 0,
                        currency: CurrencyEnum.Vnd,
                        __typename: 'Money',
                    },
                    label: 'Giảm giá',
                    __typename: 'Discount',
                },
            ],
            price: {
                currency: currencyValue,
                value: finalPrice?.value,
                __typename: 'Money',
            },
            row_total: {
                currency: CurrencyEnum.Vnd,
                value: finalPrice?.value,
                __typename: 'Money',
            },
            row_total_including_tax: {
                currency: CurrencyEnum.Vnd,
                value: finalPrice?.value,
                __typename: 'Money',
            },
            total_item_discount: {
                currency: CurrencyEnum.Vnd,
                value: 0,
                __typename: 'Money',
            },
            __typename: 'CartItemPrices',
        },
        __typename: 'ConfigurableCartItem',
    };
};

export function generateWebCartItem(item: ProductModel, variation?: Maybe<ConfigurableVariant>, quantity?: number) {
    if (item.__typename === 'ConfigurableProduct') {
        if (!isEmpty(variation)) {
            return generateConfigurableCartItem(item as unknown as ConfigurableProduct, variation);
        }
        const cartItem = generateSimpleCartItem(item, quantity);
        const updateCartItem: CartItemInterface = {
            ...cartItem,
            product: {
                ...cartItem.product,
                id: +`${item.id}.${item.id}`, // avoid to select non-single variant
            },
        };

        return updateCartItem;
    }
    return generateSimpleCartItem(item);
}
